import { FHIRModel } from '@ctw/shared/api/fhir/models/fhir-model';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import { codeableConceptLabel } from '@zeus-health/types';
import type { ExplanationOfBenefit } from 'fhir/r4';

export class ExplanationOfBenefitModel extends FHIRModel<ExplanationOfBenefit> {
  public kind = 'ExplanationOfBenefit' as const;

  public get title(): string {
    return this.icd10Codes.length > 0
      ? `ICD-10 ${this.icd10Codes.join(', ')}`
      : 'Explanation of Benefit';
  }

  /**
   * Diagnosis codes from `diagnosis[].diagnosisCodeableConcept.coding`, deduplicated.
   * Collected across all coding systems for now; ICD-10-CM system filtering to be added later.
   */
  public get icd10Codes(): Array<string> {
    const seen = new Set<string>();
    for (const d of this.resource.diagnosis ?? []) {
      for (const c of d.diagnosisCodeableConcept?.coding ?? []) {
        if (c.code) {
          seen.add(c.code);
        }
      }
    }
    return [...seen];
  }

  /**
   * Date of service: a `M/d/yyyy - M/d/yyyy` range when `billablePeriod.start`
   * and `.end` both exist and differ, otherwise the single date (`end`, falling
   * back to `start`). Mirrors `ClaimModel.serviceDate`.
   */
  public get serviceDate(): string | undefined {
    const start = this.resource.billablePeriod?.start
      ? formatFHIRDate(this.resource.billablePeriod.start)
      : undefined;
    const end = this.resource.billablePeriod?.end
      ? formatFHIRDate(this.resource.billablePeriod.end)
      : undefined;
    if (start && end && start !== end) {
      return `${start} - ${end}`;
    }
    return end ?? start;
  }

  /**
   * Type label from `ExplanationOfBenefit.type`, best-effort across coding
   * systems (preferred display, then text, then code). System-specific
   * preference to be added later.
   */
  public get claimType(): string | undefined {
    return codeableConceptLabel(this.resource.type) || undefined;
  }

  /**
   * Service codes from `item[].productOrService.coding`, deduplicated.
   * Collected across all coding systems for now; CPT/HCPCS system filtering to be added later.
   */
  public get serviceCodes(): Array<string> {
    const seen = new Set<string>();
    for (const item of this.resource.item ?? []) {
      for (const c of item.productOrService?.coding ?? []) {
        if (c.code) {
          seen.add(c.code);
        }
      }
    }
    return [...seen];
  }

  public hasProfile(profile: string): boolean {
    return this.resource.meta?.profile?.includes(profile) ?? false;
  }
}
